import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Download, Clock, CheckCircle2, AlertTriangle, Sprout } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { API, api, formatClassDate, formatTimeRange, formatDate } from "@/lib/api";
import { SUCCESS } from "@/constants/testIds";

export default function SuccessPage() {
  const { registrationId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [reg, setReg] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/registrations/${registrationId}`);
        if (!cancelled) setReg(data);
      } catch {
        // ignore
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [registrationId]);

  // Poll payment status if session_id present
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled || attempts >= 6) {
        setPolling(false);
        return;
      }
      attempts += 1;
      setPollAttempts(attempts);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPaymentStatus(data.payment_status);
        if (data.payment_status === "paid" || data.status === "expired") {
          setPolling(false);
          // refresh registration to pick up parent_payment_status
          const fresh = await api.get(`/registrations/${registrationId}`);
          setReg(fresh.data);
          return;
        }
      } catch {
        // ignore — retry
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId, registrationId]);

  if (!reg) {
    return (
      <>
        <NavBar />
        <div className="max-w-2xl mx-auto p-12">
          <div className="h-60 rounded-2xl border border-gg-border bg-gg-surface animate-pulse" />
        </div>
      </>
    );
  }

  const invoiceUrl = reg.invoice_token
    ? `${API}/registrations/${registrationId}/invoice.pdf?t=${reg.invoice_token}`
    : `${API}/registrations/${registrationId}/invoice.pdf`;
  const daysRemaining = reg.classwallet_days_remaining ?? 7;
  const parentPaid = reg.parent_payment_status === "paid";
  const parentRequired = reg.parent_attending;
  const isDirectPay = reg.payment_method === "direct";

  return (
    <>
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gg-green/15 text-gg-green mb-6">
            <Sprout size={32} />
          </div>
          <p className="font-script text-3xl text-gg-green leading-none">
            you're in
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2 text-[var(--gg-text)] leading-tight">
            Registration confirmed for {reg.student_name}
          </h1>
          <p className="mt-4 text-gg-muted">
            {reg.class_title} ·{" "}
            {reg.class_date ? formatClassDate(reg.class_date) : formatDate(reg.class_date_iso)}
            {formatTimeRange(reg.class_start_time, reg.class_end_time)
              ? ` · ${formatTimeRange(reg.class_start_time, reg.class_end_time)}`
              : ""}
          </p>
        </div>

        {/* Parent payment status (only when there's a Stripe charge pending — direct OR parent add-on) */}
        {(parentRequired || isDirectPay) && (
          <div
            className={`mt-8 rounded-2xl border p-5 sm:p-6 flex items-start gap-4 ${
              parentPaid
                ? "border-gg-green/40 bg-gg-green/8"
                : polling
                ? "border-[#D9933B]/40 bg-[#D9933B]/10"
                : "border-[#B24C4C]/40 bg-[#B24C4C]/10"
            }`}
          >
            {parentPaid ? (
              <CheckCircle2 className="text-gg-green shrink-0" />
            ) : polling ? (
              <Clock className="text-[#9b6a26] shrink-0" />
            ) : (
              <AlertTriangle className="text-[#B24C4C] shrink-0" />
            )}
            <div>
              <p className="font-semibold">
                {parentPaid
                  ? isDirectPay ? "Payment received" : "Parent add-on paid"
                  : polling
                  ? `Checking your payment… (${pollAttempts}/6)`
                  : "Payment not yet confirmed"}
              </p>
              <p className="text-sm text-gg-muted mt-1">
                {parentPaid
                  ? isDirectPay
                    ? "Your registration is fully paid and confirmed. See you in class!"
                    : "Your $12 cooking-class participation is confirmed."
                  : polling
                  ? "We're confirming your card payment with Stripe."
                  : "If you completed payment, refresh in a moment. Otherwise, contact Heather."}
              </p>
            </div>
          </div>
        )}

        {/* Receipt CTA — different framing for ESA vs Direct */}
        <div className="mt-10 rounded-2xl border-2 border-gg-green bg-gg-green/8 p-6 sm:p-8 text-center">
          <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
            {isDirectPay ? "Your records" : "Next step — ESA / ClassWallet"}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] mt-3 leading-tight">
            {isDirectPay
              ? "Download your paid receipt"
              : (reg.siblings?.length > 1 ? "Download each ClassWallet invoice" : "Download your ClassWallet invoice")}
          </h2>
          <p className="mt-3 text-gg-muted max-w-xl mx-auto">
            {isDirectPay
              ? "Marked PAID and signed by Heather Mich. Keep this for your records — no further action needed."
              : reg.siblings?.length > 1
              ? `Each student gets their own ClassWallet-ready invoice keyed to their ESA ID. Upload each one inside ClassWallet under "Pay Vendor → Graceful Garden Coaching".`
              : 'Upload this PDF inside your ClassWallet portal under "Pay Vendor → Graceful Garden Coaching" to finalize your child\'s spot.'}
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {(reg.siblings && reg.siblings.length > 0 ? reg.siblings : [{
              id: registrationId, student_name: reg.student_name,
              invoice_number: reg.invoice_number, invoice_token: reg.invoice_token,
            }]).map((sib, idx) => {
              const url = sib.invoice_token
                ? `${API}/registrations/${sib.id}/invoice.pdf?t=${sib.invoice_token}`
                : `${API}/registrations/${sib.id}/invoice.pdf`;
              return (
                <a
                  key={sib.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill btn-primary text-base px-7"
                  data-testid={idx === 0 ? SUCCESS.downloadInvoiceBtn : `download-invoice-${idx}`}
                >
                  <Download size={18} />
                  {isDirectPay ? `Receipt — ${sib.student_name}` : `${sib.student_name}'s Invoice #${sib.invoice_number}`}
                </a>
              );
            })}
          </div>
        </div>

        {/* 7-day warning — only for ESA / ClassWallet flow */}
        {!isDirectPay && (
          <div
            className="mt-8 rounded-2xl border border-[#D9933B]/40 bg-[#D9933B]/10 p-5 sm:p-6 flex items-start gap-4"
            data-testid={SUCCESS.countdownBadge}
          >
            <Clock className="text-[#9b6a26] mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-[#9b6a26]">
                You have {daysRemaining} {daysRemaining === 1 ? "day" : "days"} to upload this to ClassWallet
              </p>
              <p className="text-sm text-gg-muted mt-1 leading-relaxed">
                After {daysRemaining} {daysRemaining === 1 ? "day" : "days"} the student seat is automatically released so another family can take it. Heather will be notified when your upload is confirmed.
              </p>
            </div>
          </div>
        )}

        {/* What's next */}
        <div className="mt-10 rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
            What happens next
          </p>
          <ol className="mt-4 space-y-3 text-sm text-[var(--gg-text)]/90 list-decimal list-inside">
            {isDirectPay ? (
              <>
                <li>Save the paid receipt PDF for your records.</li>
                <li>Watch your inbox for the class-day reminder a few days out.</li>
                <li>Bring the signed waiver, a folding chair, and sun protection on class day.</li>
              </>
            ) : (
              <>
                <li>Open the PDF you just downloaded.</li>
                <li>Log in to ClassWallet → Pay Vendor → search "Graceful Garden Coaching".</li>
                <li>Upload the invoice. ClassWallet typically approves within 2–5 business days.</li>
                <li>Bring the signed waiver, a folding chair, and sun protection on class day.</li>
              </>
            )}
          </ol>
        </div>

        {/* Sibling / under-5 policy note */}
        <div className="mt-6 rounded-2xl border border-gg-border bg-gg-bg p-5 sm:p-6">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-2">A note about siblings</p>
          <p className="text-sm text-gg-muted leading-relaxed">
            Younger siblings are welcome if a paid spot is purchased for them — lap-carried babies (not yet walking) are always free.
            <strong className="block mt-2 text-[var(--gg-text)]">Children under 5 must be shadowed at all times by their parent</strong>
            to help with hands-on activities, supervision, and safety.
          </p>
        </div>

        {/* Referral */}
        <div className="mt-6 rounded-2xl border border-dashed border-gg-green bg-gg-green/8 p-6 sm:p-7">
          <p className="font-script text-2xl text-gg-green leading-none mb-1">bring a friend</p>
          <h3 className="font-heading text-2xl text-[var(--gg-text)] leading-tight">
            Save $10 — and give your friend $10 too.
          </h3>
          <p className="mt-3 text-sm text-gg-muted leading-relaxed max-w-xl">
            Know another ESA family who'd love this? Forward your confirmation email. When they
            register and mention <strong className="text-[var(--gg-text)]">{reg.parent_name?.split(" ")[0]}</strong> in
            the notes, Heather applies a <strong className="text-[var(--gg-text)]">$10 credit</strong> to both of your next classes.
          </p>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="btn-pill btn-outline inline-flex">
            Back to homepage
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
